/**
 * KB Browser — knowledge_base (Q&A จากแชทจริงที่บอทเรียนรู้ ใช้ตอบผ่าน RAG)
 * ค้นได้ทั้งคำถาม+คำตอบ (semantic) · แก้คำตอบ/ปิด-เปิด/ลบ · ข้อมูลเยอะ → แบ่งหน้า
 * filter: ทั้งหมด / ใช้บ่อย / น่าจะมั่ว + ปิดทั้งหมดที่กรอง (bulk)
 *
 * ใช้ CrudManager กลาง (โหมด paginated) — สร้างใหม่ไม่ได้ (มาจากการ sync แชท)
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { KbApi, type KbItem, type Brand } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const Api = {
  list: async () => [] as KbItem[], // ไม่ใช้ (โหมด paginated)
  create: async () => ({}),
  update: (id: number, patch: Partial<KbItem>) => KbApi.update(id, patch),
  remove: (id: number) => KbApi.remove(id),
};

const FIELDS: CrudField[] = [
  { key: 'question', label: 'คำถาม', type: 'text', disabledOnEdit: true, help: 'คำถามจากแชทจริง (แก้ไม่ได้ — เป็นตัวจับคู่ความหมาย)' },
  { key: 'answer', label: 'คำตอบ', type: 'textarea', required: true, help: 'แก้คำตอบได้ทันที — บอตใช้คำใหม่เลย (ไม่ต้อง re-embed)' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', help: 'ปิด = บอตข้ามข้อนี้ (ไม่ลบ)' },
];

const MODES = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'used', label: '🔥 ใช้บ่อย' },
  { key: 'junk', label: '⚠️ น่าจะมั่ว' },
] as const;

export default function KbPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'all' | 'used' | 'junk'>('all');
  const filter = mode === 'junk' ? 'junk' : 'all';
  const sort = mode === 'used' ? 'used' : 'default';

  const bulkMut = useMutation({
    mutationFn: (brand: Brand) => KbApi.bulk('disable', brand, '', 'junk'),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['kb'] }); toast.success(`ปิดการใช้งาน ${r.count} รายการแล้ว`); },
    onError: (e: Error) => toast.error('ปิดไม่ได้: ' + e.message),
  });

  const Filters = (brand: Brand) => (
    <div className="flex items-center gap-2 flex-wrap">
      {MODES.map((m) => (
        <Button key={m.key} size="sm" variant={mode === m.key ? 'default' : 'outline'} onClick={() => setMode(m.key)}>{m.label}</Button>
      ))}
      {mode === 'junk' && (
        <Button size="sm" variant="outline" className="ml-auto text-red-600" disabled={bulkMut.isPending} onClick={() => confirm('ปิดการใช้งานรายการ “น่าจะมั่ว” ทั้งหมด?') && bulkMut.mutate(brand)}>
          ปิดที่กรองทั้งหมด
        </Button>
      )}
    </div>
  );

  return (
    <CrudManager<KbItem>
      key={mode}
      title="🧠 KB (คลังคำตอบจากแชทจริง)"
      canCreate={false}
      hardDelete
      help="Q&A ที่บอทเรียนจากแชทจริง ใช้ตอบผ่าน RAG. ค้นได้ทั้งคำถาม+คำตอบ. แก้คำตอบ → บอตใช้ทันที. ลบ = เอาออกจากการค้นหา"
      queryKey="kb"
      api={Api}
      fields={FIELDS}
      brandScoped
      headerExtra={Filters}
      paginated={{
        pageSize: 50,
        fetch: async ({ brand, q, page }: { brand: Brand; q: string; page: number }) => {
          const r = await KbApi.list(brand, q, page, filter, sort);
          return { items: r.items, total: r.total };
        },
      }}
      rowTitle={(k) => <>{k.question}</>}
      rowBadges={(k) => (k.usage_count ? <Badge variant="outline" className="text-[10px]">🔥 ใช้ {k.usage_count}</Badge> : null)}
      rowSubtitle={(k) => <span className="line-clamp-2">{k.answer}</span>}
    />
  );
}
